import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { auditApi } from "../../services/api";
import { Table, PageHeader, Input, Button, Badge, Modal } from "../../components/ui";


// ── Action color helper ───────────────────────────────────────────────────────
const ACTION_COLORS = {
  CREATE: "var(--green)",    UPDATE: "var(--teal-300)",
  DELETE: "var(--red)",      TOGGLE: "var(--orange)",
  RESET:  "var(--gold-400)", LOGIN:  "var(--blue)",
};
const actionColor = (action) => {
  for (const [k, v] of Object.entries(ACTION_COLORS)) {
    if (action?.toUpperCase().includes(k)) return v;
  }
  return "var(--text-secondary)";
};


// ── Constants ─────────────────────────────────────────────────────────────────
const ROLE_OPTIONS = [
  { value: "",            label: "All Roles"   },
  { value: "SUPER_ADMIN", label: "Super Admin" },
  { value: "SUPPORT",     label: "Support"     },
  { value: "FINANCE",     label: "Finance"     },
  { value: "CONTENT",     label: "Content"     },
];


// ── Reusable column-header dropdown ──────────────────────────────────────────
function HeaderDropdown({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const handleOutside = (e) => {
      if (
        btnRef.current &&
        !btnRef.current.contains(e.target) &&
        panelRef.current &&
        !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleScrollOrResize = () => {
      setOpen(false);
    };

    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();

    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }

    setOpen((o) => !o);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        title="Filter"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: 4,
          cursor: "pointer",
          padding: 0,
          border: value ? "1px solid rgba(15,61,62,0.6)" : "1px solid var(--border)",
          background: value ? "rgba(15,61,62,0.12)" : "transparent",
          color: value ? "#0F3D3E" : "var(--text-muted)",
          transition: "all 0.18s",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="11"
          height="11"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.8}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.18s",
          }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "absolute",
              top: pos.top,
              left: pos.left,
              transform: "translateX(-50%)",
              zIndex: 99999,
              minWidth: 150,
              width: "max-content",
              background: "#0F3D3E",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 10,
              boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
              overflow: "hidden",
            }}
          >
            {options.map((opt) => {
              const isActive = opt.value === value;

              return (
                <div
                  key={String(opt.value)}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.10)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isActive
                      ? "rgba(255,255,255,0.12)"
                      : "transparent";
                  }}
                  style={{
                    padding: "9px 14px",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                    background: isActive ? "rgba(255,255,255,0.12)" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    transition: "background 0.15s",
                    letterSpacing: "0.3px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {opt.label}
                  {isActive && (
                    <svg
                      style={{ marginLeft: "auto" }}
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="#fff"
                      strokeWidth={3}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}


// ── Date Range Dropdown ───────────────────────────────────────────────────────
function DateRangeDropdown({ dateFrom, dateTo, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(dateFrom || "");
  const [to, setTo] = useState(dateTo || "");
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const isActive = !!(dateFrom || dateTo);

  useEffect(() => {
    setFrom(dateFrom || "");
  }, [dateFrom]);

  useEffect(() => {
    setTo(dateTo || "");
  }, [dateTo]);

  useEffect(() => {
    if (!open) return;

    const handleOutside = (e) => {
      if (
        btnRef.current &&
        !btnRef.current.contains(e.target) &&
        panelRef.current &&
        !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleScrollOrResize = () => {
      setOpen(false);
    };

    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();

    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
    }

    setOpen((o) => !o);
  };

  const handleApply = () => {
    onChange(from, to);
    setOpen(false);
  };

  const handleClear = () => {
    setFrom("");
    setTo("");
    onClear();
    setOpen(false);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        title="Filter by date range"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: 4,
          cursor: "pointer",
          padding: 0,
          border: isActive ? "1px solid #0F3D3E88" : "1px solid var(--border)",
          background: isActive ? "#0F3D3E18" : "transparent",
          color: isActive ? "#0F3D3E" : "var(--text-muted)",
          transition: "all 0.18s",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="11"
          height="11"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.8}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.18s",
          }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "absolute",
              top: pos.top,
              left: pos.left,
              zIndex: 99999,
              width: 230,
              background: "#0F3D3E",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 10,
              boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
              padding: "14px 14px 12px",
            }}
          >
            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.6)",
                  display: "block",
                  marginBottom: 5,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                From
              </label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                style={{
                  width: "100%",
                  padding: "7px 10px",
                  borderRadius: 6,
                  fontSize: 12,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(255,255,255,0.08)",
                  color: "#fff",
                  outline: "none",
                  boxSizing: "border-box",
                  colorScheme: "dark",
                }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.6)",
                  display: "block",
                  marginBottom: 5,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                To
              </label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                style={{
                  width: "100%",
                  padding: "7px 10px",
                  borderRadius: 6,
                  fontSize: 12,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(255,255,255,0.08)",
                  color: "#fff",
                  outline: "none",
                  boxSizing: "border-box",
                  colorScheme: "dark",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={handleApply}
                style={{
                  flex: 1,
                  padding: "7px 0",
                  borderRadius: 6,
                  border: "none",
                  background: "rgba(255,255,255,0.18)",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Apply
              </button>
              <button
                onClick={handleClear}
                style={{
                  flex: 1,
                  padding: "7px 0",
                  borderRadius: 6,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "transparent",
                  color: "rgba(255,255,255,0.6)",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}


// ── Log Detail Modal ──────────────────────────────────────────────────────────
function LogDetail({ log, onClose }) {
  if (!log) return null;
  return (
    <Modal open={!!log} onClose={onClose} title="Audit Log Detail" width={500}>
      {[
        ["Time",        log.createdAt ? new Date(log.createdAt).toLocaleString("en-IN") : "—"],
        ["Admin",       log.adminUsername],
        ["Role",        log.adminRole?.replace("ROLE_", "")],
        ["Action",      log.action],
        ["Entity Type", log.entityType],
        ["Entity ID",   log.entityId],
        ["IP Address",  log.ipAddress],
      ].map(([k, v]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>{k}</span>
          <span style={{ fontSize: 13, color: "var(--text-primary)", textAlign: "right", wordBreak: "break-all", maxWidth: "60%" }}>{v ?? "—"}</span>
        </div>
      ))}
      {log.metadata && Object.keys(log.metadata).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>Metadata</div>
          <div style={{ background: "var(--bg-input)", borderRadius: "var(--radius)", padding: 12, fontFamily: "monospace", fontSize: 12, color: "var(--text-secondary)", whiteSpace: "pre-wrap" }}>
            {JSON.stringify(log.metadata, null, 2)}
          </div>
        </div>
      )}
    </Modal>
  );
}


// ── Main Component ────────────────────────────────────────────────────────────
export default function AuditLogs() {
  const [data,         setData]         = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [page,         setPage]         = useState(0);
  const [search,       setSearch]       = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [roleFilter,   setRoleFilter]   = useState("");
  const [dateFrom,     setDateFrom]     = useState("");
  const [dateTo,       setDateTo]       = useState("");
  const [loading,      setLoading]      = useState(true);
  const [selected,     setSelected]     = useState(null);

  const load = useCallback(async (pg = page) => {
    setLoading(true);
    try {
      const r = await auditApi.getAll(pg, search, actionFilter, entityFilter);
      const d = r.data.data;
      let content = Array.isArray(d) ? d : (d?.content || []);

      // Frontend role filter
      if (roleFilter) {
        content = content.filter(log =>
          log.adminRole?.toUpperCase().includes(roleFilter.toUpperCase())
        );
      }

      // Frontend date range filter
      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        content = content.filter(log => new Date(log.createdAt) >= from);
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        content = content.filter(log => new Date(log.createdAt) <= to);
      }

      const PAGE_SIZE = 20; // must match your API page size
      const filteredTotal = content.length;
      const isClientFiltered = !!(roleFilter || dateFrom || dateTo);

      if (Array.isArray(d)) {
        setData({ content, totalPages: 1, totalElements: filteredTotal });
      } else if (isClientFiltered) {
        // When client-side filtering is active, derive pages from filtered count
        setData({
          ...d,
          content,
          totalElements: filteredTotal,
          totalPages: Math.max(1, Math.ceil(filteredTotal / PAGE_SIZE)),
        });
      } else {
        // No client filter — trust the API's pagination fully
        setData({ ...d, content, totalElements: filteredTotal });
      }
    } catch {
      setData({ content: [], totalPages: 0, totalElements: 0 });
    } finally {
      setLoading(false);
    }
  }, [page, search, actionFilter, entityFilter, roleFilter, dateFrom, dateTo]);

  useEffect(() => { load(page); }, [page, actionFilter, entityFilter, roleFilter, dateFrom, dateTo]);

  useEffect(() => {
      const timer = setTimeout(() => {
        setPage(0);
        load(0);
      }, search ? 400 : 0);  // debounce while typing, instant when cleared
      return () => clearTimeout(timer);
    }, [search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    load(0);
  };

  const handleClear = () => {
      setSearch("");          //triggers the new search useEffect instantly
      setActionFilter("");
      setEntityFilter("");
      setRoleFilter("");
      setDateFrom("");
      setDateTo("");
      setPage(0);
    };

  const columns = [

    {
      key: "id", label: "Sr.",
      render: (v) => {
        const index = data.content.findIndex(item => item.id === v);
        return (
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", minWidth: 28, display: "inline-block" }}>
            {page * 20 + index + 1}
          </span>
        );
      },
    },

    {
      key: "createdAt",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Date</span>
          <DateRangeDropdown
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={(from, to) => { setDateFrom(from); setDateTo(to); setPage(0); }}
            onClear={() => { setDateFrom(""); setDateTo(""); setPage(0); }}
          />
        </div>
      ),
      render: (v) => v ? new Date(v).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—",
    },
    {
      key: "adminUsername", label: "Admin",
      render: (v) => <span style={{ fontWeight: 500 }}>{v || "—"}</span>,
    },
    {
      key: "adminRole",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Role</span>
          <HeaderDropdown
            value={roleFilter}
            onChange={(v) => { setRoleFilter(v); setPage(0); }}
            options={ROLE_OPTIONS}
          />
        </div>
      ),
      render: (v) => <span style={{ fontSize: 11, color: "var(--teal-300)" }}>{v?.replace("ROLE_", "") || "—"}</span>,
    },
    {
      key: "action", label: "Action",
      render: (v) => <span style={{ fontWeight: 600, fontSize: 12, color: actionColor(v) }}>{v}</span>,
    },
    {
      key: "entityType", label: "Entity",
      render: (v) => <Badge variant="default">{v || "—"}</Badge>,
    },
    {
      key: "ipAddress", label: "IP",
      render: (v) => <span style={{ fontSize: 11, fontFamily: "monospace", color: "var(--text-muted)" }}>{v || "—"}</span>,
    },
    {
      key: "id", label: "Details",
      render: (_, row) => (
        <button
          onClick={() => setSelected(row)}
          title="View Details"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", padding: 4, borderRadius: 6,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            transition: "color 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = "#0F3D3E"}
          onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ),
    },
  ];

  const hasFilters = search || actionFilter || entityFilter || roleFilter || dateFrom || dateTo;

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <PageHeader title="Audit Logs" subtitle="Track every admin action across the platform" />

      <form onSubmit={handleSearch} style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <Input
          placeholder="Search by admin username…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 220 }}
        />
        <Button type="submit">Search</Button>
        <Button variant="ghost" type="button" onClick={handleClear}>Clear</Button>
      </form>

      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", overflow: "hidden" }}>

        {/* Header bar */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{data.totalElements} entries</span>
          {hasFilters && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {search       && <span style={{ fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E", borderRadius: 20, padding: "2px 10px", fontWeight: 600 }}>👤 {search}</span>}
              {actionFilter && <span style={{ fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E", borderRadius: 20, padding: "2px 10px", fontWeight: 600 }}>⚡ {actionFilter}</span>}
              {entityFilter && <span style={{ fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E", borderRadius: 20, padding: "2px 10px", fontWeight: 600 }}>📦 {entityFilter}</span>}
              {roleFilter   && <span style={{ fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E", borderRadius: 20, padding: "2px 10px", fontWeight: 600 }}>🛡 {roleFilter}</span>}
              {(dateFrom || dateTo) && (
                <span style={{ fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E", borderRadius: 20, padding: "2px 10px", fontWeight: 600 }}>
                  📅 {dateFrom || "…"} → {dateTo || "…"}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        {loading
          ? <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
              <div style={{ width: 32, height: 32, margin: "0 auto 10px", border: "3px solid var(--border)", borderTop: "3px solid #0F3D3E", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
              Loading…
            </div>
          : <Table columns={columns} data={data.content} emptyText="No audit logs found" />
        }

        {/* Pagination */}
        <div style={{
          padding: "14px 20px",
          borderTop: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        }}>
          {(() => {
            const totalPages = data.totalPages;
            if (totalPages <= 1 || data.totalElements === 0) return null;

            const getPages = () => {
              if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
              const pages = [];
              if (page <= 3) {
                pages.push(0, 1, 2, 3, 4, "...", totalPages - 1);
              } else if (page >= totalPages - 4) {
                pages.push(0, "...", totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1);
              } else {
                pages.push(0, "...", page - 1, page, page + 1, "...", totalPages - 1);
              }
              return pages;
            };

            const btnStyle = (isActive, isDisabled) => ({
              minWidth: 34, height: 34, padding: "0 10px",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              border: isActive ? "none" : "1px solid var(--border)",
              borderRadius: 8,
              background: isActive ? "#0F3D3E" : "transparent",
              color: isActive ? "#fff" : isDisabled ? "var(--text-muted)" : "var(--text-primary)",
              fontSize: 13, fontWeight: isActive ? 700 : 500,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.45 : 1,
              transition: "all 0.15s",
              fontFamily: "var(--font-body)",
            });

            return (
              <>
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={btnStyle(false, page === 0)}>
                  ← Prev
                </button>

                {getPages().map((p, i) =>
                  p === "..." ? (
                    <span key={`dots-${i}`} style={{ padding: "0 4px", color: "var(--text-muted)", fontSize: 14, userSelect: "none" }}>…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      style={btnStyle(page === p, false)}
                      onMouseEnter={e => { if (page !== p) e.currentTarget.style.background = "rgba(15,61,62,0.08)"; }}
                      onMouseLeave={e => { if (page !== p) e.currentTarget.style.background = "transparent"; }}
                    >
                      {p + 1}
                    </button>
                  )
                )}

                <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} style={btnStyle(false, page >= totalPages - 1)}>
                  Next →
                </button>
              </>
            );
          })()}
        </div>
      </div>

      <LogDetail log={selected} onClose={() => setSelected(null)} />
    </div>
  );
}